import { useEffect } from "react";
import { Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import { clarityOneLoginUrlWithRedirect } from "@shared/clarityOneAuthRedirect";
import { formatDate } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Download, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ClarityOneDocumentsPage() {
  const [, setLocation] = useLocation();
  const { data: me, isLoading: meLoading } = trpc.clarityOneAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });
  const { data, isLoading } = trpc.clarityOneRecords.listProjectDocuments.useQuery(undefined, {
    enabled: !!me,
    refetchOnWindowFocus: false,
  });
  const getDoc = trpc.clarityOneRecords.getDocumentDownloadUrl.useMutation({
    onSuccess: (res) => {
      window.open(res.url, "_blank", "noopener,noreferrer");
    },
    onError: (e) => toast.error(e.message || "Document unavailable"),
  });

  useEffect(() => {
    if (!meLoading && !me) setLocation(clarityOneLoginUrlWithRedirect("/documents"));
  }, [meLoading, me, setLocation]);

  if (meLoading || !me) {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" />
        </div>
      </ClarityOneLayout>
    );
  }

  return (
    <ClarityOneLayout showNav>
      <Link
        href="/home"
        className="inline-flex items-center gap-1 text-sm text-[#8B7355] no-underline mb-6 hover:text-[#1A2744]"
      >
        <ChevronLeft className="h-4 w-4" /> Home
      </Link>
      <h1 className="text-3xl font-light text-[#1A2744] tracking-tight mb-3">Project Documents</h1>
      <p className="text-sm text-[#8B7355] mb-10 max-w-xl leading-relaxed">
        Technical drawings, floor plans, markups, and project PDFs shared with you.
      </p>

      {isLoading ? (
        <Loader2 className="h-6 w-6 animate-spin text-[#B8956E]" />
      ) : !data?.length ? (
        <p className="text-sm text-[#8B7355]">No project documents have been shared yet.</p>
      ) : (
        <div className="space-y-10">
          {data.map((project) => (
            <section key={project.projectId}>
              <h2 className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#B8956E] mb-1">
                {project.projectRef}
              </h2>
              <p className="text-lg font-medium text-[#1A2744] mb-4">{project.projectName}</p>
              <div className="space-y-3">
                {project.documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="bg-white rounded-2xl border border-[#E8E4DC] px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1A2744] truncate">{doc.name}</p>
                      <p className="text-sm text-[#8B7355] mt-0.5">
                        {doc.documentType}
                        {doc.uploadDate ? ` · ${formatDate(doc.uploadDate)}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-[#E8E4DC]"
                        disabled={getDoc.isPending}
                        onClick={() => getDoc.mutate({ fileId: doc.id })}
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                        View
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="bg-[#1A2744] text-white hover:bg-[#1A2744]/90"
                        disabled={getDoc.isPending}
                        onClick={() => getDoc.mutate({ fileId: doc.id })}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </ClarityOneLayout>
  );
}
